import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  COMMUNITY_REVIEW_ALERTS,
  COMMUNITY_SETTINGS_PAGE_TYPE,
  EMAIL_NOTIFICATION_TYPE,
} from 'components/Settings/constants';
import { CommunityPreferenceNotification, CommunityPreferenceType } from 'types/communities';

import CommunityReviewAlertsRecipientsSettings from './CommunityReviewAlertsRecipientsSettings';
import { CommunityReviewAlertScope, DEFAULT_ALERT_MODE_SETTINGS_BY_SCOPE } from './constants';
import { CommunityReviewAlertScopeSettings } from './types';
import EmailNotificationsRecipientsModal from '../EmailNotificationsRecipientsModal';

type Props = {
  isOpen: boolean;
  loading: boolean;
  title: string;
  onClose: () => void;
  communitiesToModify: CommunityPreferenceType[];
  onSave: (
    notificationType: number,
    newStatus: number,
    preferenceNotifications: CommunityPreferenceNotification[],
    alertModeSettings: Record<CommunityReviewAlertScope, CommunityReviewAlertScopeSettings>,
    alertFlags: Record<CommunityReviewAlertScope, boolean>
  ) => void;
};

const CommunityReviewAlertsRecipientsModal = ({
  isOpen,
  loading,
  title,
  onClose,
  communitiesToModify,
  onSave,
}: Props) => {
  const [activeTab, setActiveTab] = useState<CommunityReviewAlertScope>(CommunityReviewAlertScope.COMMUNITY);
  const [alertFlags, setAlertFlags] = useState<Record<CommunityReviewAlertScope, boolean>>({
    [CommunityReviewAlertScope.COMMUNITY]: false,
    [CommunityReviewAlertScope.COMPETITOR]: false,
  });
  const [alertModeSettings, setAlertModeSettings] = useState<
    Record<CommunityReviewAlertScope, CommunityReviewAlertScopeSettings>
  >(DEFAULT_ALERT_MODE_SETTINGS_BY_SCOPE);

  const resetToDefaultTab = useCallback(() => {
    setActiveTab(CommunityReviewAlertScope.COMMUNITY);
  }, []);

  useEffect(() => {
    if (isOpen) {
      resetToDefaultTab();
      setAlertModeSettings({
        [CommunityReviewAlertScope.COMMUNITY]: {
          ...DEFAULT_ALERT_MODE_SETTINGS_BY_SCOPE[CommunityReviewAlertScope.COMMUNITY],
        },
        [CommunityReviewAlertScope.COMPETITOR]: {
          ...DEFAULT_ALERT_MODE_SETTINGS_BY_SCOPE[CommunityReviewAlertScope.COMPETITOR],
        },
      });
    } else {
      resetToDefaultTab();
    }
  }, [isOpen, resetToDefaultTab]);

  const getInitialStatusValue = useCallback(
    ({ communitiesToModify: communities }: { communitiesToModify: CommunityPreferenceType[] }) => {
      const hasCommunityAlerts = Boolean(communities[0]?.preferences?.community_review_alert_notifications);
      const hasCompetitorAlerts = Boolean(communities[0]?.preferences?.competitor_review_alert_notifications);

      return hasCommunityAlerts || hasCompetitorAlerts
        ? COMMUNITY_REVIEW_ALERTS.statusElements.ON.value
        : COMMUNITY_REVIEW_ALERTS.statusElements.OFF.value;
    },
    []
  );

  const renderSettingsSection = useCallback(
    ({ setStatusValue }: { setStatusValue: (nextStatus: number) => void }) => (
      <CommunityReviewAlertsRecipientsSettings
        isOpen={isOpen}
        activeTab={activeTab}
        communitiesToModify={communitiesToModify}
        statusElements={COMMUNITY_REVIEW_ALERTS.statusElements}
        onOverallStatusChange={setStatusValue}
        onActiveTabChange={setActiveTab}
        onAlertFlagsChange={setAlertFlags}
        onAlertModeSettingsChange={setAlertModeSettings}
      />
    ),
    [activeTab, isOpen, communitiesToModify]
  );

  const recipientsNotificationType =
    activeTab === CommunityReviewAlertScope.COMMUNITY
      ? EMAIL_NOTIFICATION_TYPE.COMMUNITY_REVIEW_ALERTS
      : EMAIL_NOTIFICATION_TYPE.COMPETITOR_REVIEW_ALERTS;
  const showRecipients = useMemo(() => alertFlags[activeTab], [alertFlags, activeTab]);

  const handleClose = () => {
    resetToDefaultTab();
    onClose();
  };

  return (
    <EmailNotificationsRecipientsModal
      onClose={handleClose}
      isOpen={isOpen}
      loading={loading}
      title={title}
      width={1000}
      communitiesToModify={communitiesToModify}
      onSave={(newStatus, preferenceNotifications) =>
        onSave(
          recipientsNotificationType,
          alertFlags[activeTab]
            ? COMMUNITY_REVIEW_ALERTS.statusElements.ON.value
            : COMMUNITY_REVIEW_ALERTS.statusElements.OFF.value,
          preferenceNotifications,
          alertModeSettings,
          alertFlags
        )
      }
      settingsPageType={COMMUNITY_SETTINGS_PAGE_TYPE.COMMUNITY_REVIEW_ALERTS}
      recipientsNotificationType={recipientsNotificationType}
      showRecipients={showRecipients}
      getInitialStatusValue={getInitialStatusValue}
      renderSettingsSection={renderSettingsSection}
    />
  );
};

export default CommunityReviewAlertsRecipientsModal;
